import qs from 'qs';
import toArray from '@stripcash/ui-kit/utils/toArray';

import {
  userSchemeTypeSelector,
  isCurrentUserAccountantSelector,
  isCurrentUserHeadManagerSelector,
  isCurrentUserSimpleManagerSelector,
  isCurrentUserAdminSelector,
  isUserApprovedSelector,
  isUserLoggedInSelector,
  isCurrentUserCustomManagerSelector,
  isCurrentUserLovescapeManagerSelector,
  isCurrentUserTrialManagerSelector,
  isCurrentUserHasStripchatSchemeSelector,
} from 'users/selectors';
import { SchemeType } from 'types/user';
import {
  ANALYTICS_STATISTICS_PATH,
  FORBIDDEN_PATH,
  LOGIN_PATH,
  OVERVIEW_DASHBOARD_PATH,
} from 'common/selectors/sidebar/common';
import {
  MODELS_TRAFFIC_PATH,
  OVERVIEW_ACCOUNT_PATH,
} from 'common/selectors/sidebar/affiliates';
import { PAYOUTS_PAYOUTS_CONTROL_PATH } from 'common/selectors/sidebar/manager';
import CookieAccepts from 'common/constants/cookieAccepts';

import not from './not';

const ifElse =
  (condition, onTrue, onFalse) =>
  (...args) =>
    condition(...args) ? onTrue(...args) : onFalse(...args);
const always = (value) => () => value;
const pass = always(null);
const inCase = (condition, onTrue) => ifElse(condition, onTrue, pass);

export const any =
  (...conditions) =>
  (...args) =>
    conditions.reduce(
      (result, condition) => result || condition(...args),
      pass(),
    );

const all =
  (...fns) =>
  (...args) =>
    fns.every((fn) => fn(...args));
const schemeTypeIn = (schemeTypes) => (state) =>
  schemeTypes.includes(userSchemeTypeSelector(state));

export const formatUrl = ({ pathname, query = {}, hash = '' }) =>
  `${[pathname, qs.stringify(query)].filter(Boolean).join('?')}${hash}`;

// conditions
export const notLoggedToLogin = inCase(
  not(isUserLoggedInSelector),
  ({ router: { location } }) =>
    formatUrl({
      pathname: LOGIN_PATH,
      query: { redirectTo: formatUrl(location) },
    }),
);

export const loggedTo = (path) => inCase(isUserLoggedInSelector, always(path));
export const forbidden = always(FORBIDDEN_PATH);
export const statistics = always(ANALYTICS_STATISTICS_PATH);
export const dashboard = always(OVERVIEW_DASHBOARD_PATH);
export const modelsTraffic = always(MODELS_TRAFFIC_PATH);
export const notApprovedToAccount = any(
  notLoggedToLogin,
  inCase(not(isUserApprovedSelector), always(OVERVIEW_ACCOUNT_PATH)),
);
const accountantToPayoutsControl = inCase(
  isCurrentUserAccountantSelector,
  always(PAYOUTS_PAYOUTS_CONTROL_PATH),
);

const hasFlag = (place: string) => (flagNames: string[]) => (state) =>
  toArray(flagNames).some((flagName) => state.config[place][flagName]);

const hasAclFlag = hasFlag('acl');
const hasFeatureFlag = hasFlag('features');

export const withoutFeatureFlag = (featureFlagName, fn) =>
  inCase(not(hasFeatureFlag(featureFlagName)), fn);

export const withoutAclToForbidden = (...aclFlagName) =>
  inCase(not(hasAclFlag(aclFlagName)), forbidden);

export const withAclToForbidden = (...aclFlagName) =>
  inCase(hasAclFlag(aclFlagName), forbidden);

export const mediaBuyerStudioOrModelToModelsTraffic = inCase(
  schemeTypeIn([SchemeType.MediaBuyerStudio, SchemeType.MediaBuyerModel]),
  modelsTraffic,
);

export const notMediaBuyerStudioOrModelToDashboard = any(
  notApprovedToAccount,
  inCase(
    not(
      schemeTypeIn([SchemeType.MediaBuyerStudio, SchemeType.MediaBuyerModel]),
    ),
    dashboard,
  ),
);

export const onlyAffiliatesWithStripchatScheme = any(
  notLoggedToLogin,
  inCase(not(isCurrentUserHasStripchatSchemeSelector), statistics),
);

// all affiliates (EXCLUDE (media buyer studio and media buyer model) - affiliate)
export const onlyRegularAffiliates = any(
  notApprovedToAccount,
  mediaBuyerStudioOrModelToModelsTraffic,
);

export const commonAccessForStripchatScheme = any(
  onlyRegularAffiliates,
  onlyAffiliatesWithStripchatScheme,
);

export const notLoggedOrAccountantToPayoutsControl = any(
  notLoggedToLogin,
  accountantToPayoutsControl,
);

export const customManagerToStatistics = any(
  notLoggedToLogin,
  inCase(isCurrentUserCustomManagerSelector, statistics),
);

export const notStripcashManagerToStatistics = any(
  notLoggedToLogin,
  accountantToPayoutsControl,
  inCase(
    all(
      not(isCurrentUserSimpleManagerSelector),
      not(isCurrentUserAdminSelector),
    ),
    statistics,
  ),
);

export const notStripcashAndTrialManagerToStatistics = any(
  notLoggedToLogin,
  accountantToPayoutsControl,
  inCase(
    all(
      not(isCurrentUserSimpleManagerSelector),
      not(isCurrentUserAdminSelector),
      not(isCurrentUserTrialManagerSelector),
    ),
    statistics,
  ),
);

export const cplSetupAccess = any(
  notLoggedToLogin,
  accountantToPayoutsControl,
  inCase(
    all(
      not(isCurrentUserSimpleManagerSelector),
      not(isCurrentUserAdminSelector),
      not(isCurrentUserLovescapeManagerSelector),
    ),
    statistics,
  ),
);

export const trialManagerToStatistics = inCase(
  isCurrentUserTrialManagerSelector,
  statistics,
);

export const onlyAnyManagers = any(
  notLoggedToLogin,
  accountantToPayoutsControl,
);

export const notHeadManagerToStatistics = any(
  notLoggedToLogin,
  inCase(not(isCurrentUserHeadManagerSelector), statistics),
);

export const internalSchemesToForbidden = inCase(
  schemeTypeIn([
    SchemeType.InternalTraffic,
    SchemeType.MediaBuyer,
    SchemeType.PayPerClick,
  ]),
  forbidden,
);

export const notManagersToForbiddenMBReport = any(
  notLoggedToLogin,
  inCase(
    not(any(isCurrentUserSimpleManagerSelector, isCurrentUserAdminSelector)),
    statistics,
  ),
);

export const unlessRevenueShare = (fn) =>
  inCase(not(schemeTypeIn([SchemeType.RevenueShare])), fn);

export const notPPStoForbiddenMemberInspector = inCase(
  not(schemeTypeIn([SchemeType.RevenueShare, SchemeType.PpsRevenueShare])),
  forbidden,
);

// acccess by cookie
const unlockAndGoAhead = (cookie) => (state, isNavigateCheck) => {
  if (isNavigateCheck) {
    // allow for those dudes that already knows this page
    document.cookie = `${cookie};path=/;domain=.${document.location.hostname}`;
    document.location.reload();

    return null;
  }

  // hide in sidebar until you visited page directly
  return forbidden();
};

export const whiteLabelAccess = any(
  notApprovedToAccount,
  unlessRevenueShare(
    withoutFeatureFlag(
      'whiteLabels',
      unlockAndGoAhead(CookieAccepts.Whitelabels),
    ),
  ),
);

export const smartLinkAccess = any(
  notApprovedToAccount,
  withoutFeatureFlag(
    'smartpopunders',
    unlockAndGoAhead(CookieAccepts.Smartpopunders),
  ),
);
